﻿import { Component } from '@theme/component';
import { ThemeEvents, VariantUpdateEvent, ZoomMediaSelectedEvent } from '@theme/events';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });
    this.addEventListener('click', this.#handleMediaClick, { signal });
  }

  #controller = new AbortController();

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }


  /**
   * Opens zoom when the actual product image area is clicked.
   * This is a fallback for mobile browsers when the declarative on:click handler
   * or the transparent zoom button is not the final click target.
   * @param {MouseEvent} event - The click event.
   */
  #handleMediaClick = (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;
    if (target.closest('zoom-dialog')) return;
    if (target.closest('slideshow-controls, slideshow-arrows, .dialog-thumbnails-list-container')) return;

    const clickedZoomButton = target.closest('.product-media-container__zoom-button');
    const mediaContainer = target.closest(
      'slideshow-slide.product-media-container--image, li.product-media-container--image'
    );

    if (!clickedZoomButton && !mediaContainer) return;

    const container = /** @type {HTMLElement | null} */ (
      mediaContainer || clickedZoomButton?.closest('slideshow-slide.product-media-container--image, li.product-media-container--image')
    );
    if (!container) return;

    const siblings = Array.from(container.parentElement?.children || []).filter(
      (element) =>
        element instanceof HTMLElement &&
        element.classList.contains('product-media-container')
    );
    const index = siblings.indexOf(container);

    if (index < 0) return;
    this.zoom(index, /** @type {PointerEvent} */ (event));
  };
  /**
   * Handles a variant update event by replacing the current media gallery with a new one.
   *
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #handleVariantUpdate = (event) => {
    const source = event.detail.data.html;

    if (!source) return;
    const newMediaGallery = source.querySelector('media-gallery');

    if (!newMediaGallery) return;

    this.replaceWith(newMediaGallery);
  };

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  /**
   * Preloads an image.
   * @param {number} index - The index of the media to preload.
   */
  preloadImage(index) {
    const zoomDialogMedia = this.refs.zoomDialogComponent?.refs.media[index];
    if (!zoomDialogMedia) return;

    this.refs.zoomDialogComponent?.loadHighResolutionImage(zoomDialogMedia);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
